// src/AskAIModal.tsx
// NOTE: Update frontend/src/__tests__/AskAIModal.test.tsx and run
// `../scripts/test-frontend.sh AskAIModal` when editing this modal.
import React, { useMemo, useState, useEffect } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { Modal, Button, Input, List, Typography, Space, Tag, message, Tooltip, Checkbox, Spin } from 'antd';
import { CopyOutlined, SendOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;
const { Text } = Typography;

const API_BASE = resolveFrontendApiBase();

export interface AskAIMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ReviewContextEntry {
  label: string;
  fullText?: string;
  replacements?: Array<{ from?: string; to?: string; method?: string }>;
}

export interface AskAIInvocationPayload {
  chapterGuid: string;
  variant: string;
  variantLabel: string;
  chapterHtml: string;
  originalChapterHtml?: string;
  initialReviewContext?: ReviewContextEntry | null;
  autoFetchReviewContext?: boolean;
}

export interface AskAIModalProps {
  visible: boolean;
  onClose: () => void;
  bookId: string;
  chapterGuid: string;
  variant: string; // 'original' | 'original_proofread' | translated_... | proofread_...
  variantLabel: string;
  chapterHtml: string; // full HTML for the variant being viewed
  originalChapterHtml?: string; // optional HTML for the original chapter
  auth: any;
  defaultModelId?: string; // optional override; defaults to Gemini 3.0 Pro when unspecified
  initialReviewContext?: ReviewContextEntry | null;
  autoFetchReviewContext?: boolean;
}


const MAX_HISTORY_PAIRS = 2;
const DEFAULT_ASK_AI_MODEL_ID = 'Gemini3Pro';

const AskAIModal: React.FC<AskAIModalProps> = ({
  visible,
  onClose,
  bookId,
  chapterGuid,
  variant,
  variantLabel,
  chapterHtml,
  originalChapterHtml,
  auth,
  defaultModelId,
  initialReviewContext = null,
  autoFetchReviewContext = true,
}) => {
  const [messages, setMessages] = useState<AskAIMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [includeOriginal, setIncludeOriginal] = useState(false);
  const [reviewContext, setReviewContext] = useState<ReviewContextEntry | null>(initialReviewContext);
  const [reviewContextLoading, setReviewContextLoading] = useState(false);
  const [includeReviewContext, setIncludeReviewContext] = useState(!!initialReviewContext);
  const resolvedModelId = useMemo(() => {
    if (typeof defaultModelId === 'string' && defaultModelId.trim()) {
      return defaultModelId.trim();
    }
    return DEFAULT_ASK_AI_MODEL_ID;
  }, [defaultModelId]);

  const trimmedHistory = useMemo(() => {
    // Keep last 2 Q/A pairs (i.e., last 4 messages), if available
    if (messages.length <= MAX_HISTORY_PAIRS * 2) return messages;
    return messages.slice(messages.length - MAX_HISTORY_PAIRS * 2);
  }, [messages]);

  const isTranslatedVariant = useMemo(() => {
    return variant.startsWith('translated_') || variant.startsWith('proofread_');
  }, [variant]);

  const canIncludeOriginal = useMemo(() => {
    if (!isTranslatedVariant) return false;
    return !!(originalChapterHtml && originalChapterHtml.trim());
  }, [isTranslatedVariant, originalChapterHtml]);

  const handleSend = async () => {
    const q = question.trim();
    if (!q) return;
    if (!auth?.currentUser) {
      message.error('You must be logged in');
      return;
    }
    setSending(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const includeOriginalFlag = canIncludeOriginal && includeOriginal;
      const reviewPayload = includeReviewContext && reviewContext
        ? {
            validatorFullText: reviewContext.fullText,
            validatorReplacements: reviewContext.replacements,
            reviewContextLabel: reviewContext.label,
          }
        : {};
      const { data } = await axios.post(
        `${API_BASE}/admin/ask-ai`,
        {
          bookId,
          chapterGuid,
          variant,
          chapterHtml,
          variantLabel,
          messages: trimmedHistory,
          question: q,
          modelId: resolvedModelId,
          includeOriginalChapter: includeOriginalFlag,
          originalChapterHtml: includeOriginalFlag ? originalChapterHtml : undefined,
          ...reviewPayload,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (typeof data?.answer === 'string') {
        setMessages(prev => [...prev, { role: 'user', content: q }, { role: 'assistant', content: data.answer }]);
        setQuestion('');
      } else {
        message.error('No answer received from AI');
      }
    } catch (err: any) {
      console.error('Ask AI error:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Error contacting AI service';
      message.error(detail);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      setMessages([]);
      setQuestion('');
      setIncludeOriginal(false);
      setReviewContext(initialReviewContext || null);
      setIncludeReviewContext(!!initialReviewContext);
      setReviewContextLoading(false);
      return;
    }
    if (initialReviewContext) {
      setReviewContext(initialReviewContext);
      setIncludeReviewContext(true);
    } else if (!autoFetchReviewContext) {
      setReviewContext(null);
      setIncludeReviewContext(false);
    }
  }, [visible, initialReviewContext, autoFetchReviewContext]);

  useEffect(() => {
    if (!visible || !autoFetchReviewContext || initialReviewContext) {
      return;
    }
    if (!auth?.currentUser) {
      setReviewContext(null);
      setIncludeReviewContext(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setReviewContextLoading(true);
      try {
        const token = await auth.currentUser.getIdToken();
        const { data } = await axios.get(
          `${API_BASE}/api/admin/ai-logs`,
          {
            params: {
              bookId,
              chapterGuid,
              stage: 'validator',
              limit: 1,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (cancelled) return;
        const entry = Array.isArray(data?.entries) ? data.entries[0] : null;
        if (entry && (entry.fullText || (entry.replacements && entry.replacements.length))) {
          setReviewContext({
            label: 'Validator Context',
            fullText: entry.fullText,
            replacements: entry.replacements || [],
          });
          setIncludeReviewContext(true);
        } else {
          setReviewContext(null);
          setIncludeReviewContext(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load validator context', err);
          setReviewContext(null);
          setIncludeReviewContext(false);
        }
      } finally {
        if (!cancelled) {
          setReviewContextLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [visible, auth, bookId, chapterGuid, autoFetchReviewContext, initialReviewContext]);

  const trimmedQuestion = question.trim();
  const hasQuestion = trimmedQuestion.length > 0;
  const includedContextCount = Number(canIncludeOriginal && includeOriginal) + Number(Boolean(includeReviewContext && reviewContext));
  const replacementCount = reviewContext?.replacements?.length || 0;
  const footerSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite">
      <span>
        <strong>{messages.length}</strong> messages
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>{includedContextCount}</strong> context source{includedContextCount === 1 ? '' : 's'} included
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      {hasQuestion ? (
        <span>
          <strong>{trimmedQuestion.length}</strong> character question ready
        </span>
      ) : (
        <span className="translation-modal-footer-summary__warning">Question needed</span>
      )}
      {!auth?.currentUser ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Login required</span>
        </>
      ) : null}
    </div>
  );

  return (
    <Modal
      title={
        <div style={{ width: '100%', textAlign: 'center' }}>
          <Space align="center">
            <QuestionCircleOutlined />
            <span>Ask AI</span>
            <Tag
              style={{
                marginInlineStart: 2,
                borderColor: 'var(--border-info)',
                background: 'var(--surface-info)',
                color: 'var(--text-info)',
                fontWeight: 700,
              }}
            >
              {variantLabel}
            </Tag>
          </Space>
        </div>
      }
      open={visible}
      onCancel={onClose}
      centered
      className="translation-modal-footer-stable"
      width="min(820px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      footer={[
        <React.Fragment key="summary">{footerSummary}</React.Fragment>,
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          loading={sending}
          disabled={!hasQuestion}
          onClick={handleSend}
        >
          Send
        </Button>,
      ]}
    >
      <div className="translation-modal-body">
        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Conversation</span>
              <h3>Q&A history</h3>
              <p>Ask focused questions about this chapter. Recent messages are included for follow-up context.</p>
            </div>
            <Tag
              style={{
                borderColor: messages.length ? 'var(--border-success)' : 'var(--border-subtle)',
                background: messages.length ? 'var(--surface-success)' : 'var(--surface-muted)',
                color: messages.length ? 'var(--text-success)' : 'var(--text-secondary)',
                fontWeight: 700,
              }}
            >
              {messages.length ? `${messages.length} messages` : 'Empty'}
            </Tag>
          </div>
          <List
            size="small"
            dataSource={messages}
            locale={{ emptyText: 'No messages yet. Ask a question below.' }}
            renderItem={(m, idx) => (
              <List.Item
                key={idx}
                style={{
                  padding: '8px 0',
                  borderBlockEnd: idx === messages.length - 1 ? '0' : '1px solid color-mix(in srgb, var(--border-subtle) 58%, transparent)',
                }}
              >
                <div style={{ width: '100%' }}>
                  <Text strong style={{ color: 'var(--text-main)' }}>{m.role === 'user' ? 'Q:' : 'A:'}</Text>
                  <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-main)', lineHeight: 1.45 }}>{m.content}</div>
                  {m.role === 'assistant' && (
                    <div style={{ textAlign: 'right', marginTop: 4 }}>
                      <Tooltip title="Copy answer">
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => navigator.clipboard.writeText(m.content)}
                        >
                          Copy
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </List.Item>
            )}
            style={{
              maxHeight: 300,
              overflowY: 'auto',
              paddingRight: 4,
              borderRadius: 8,
              background: messages.length ? 'transparent' : 'color-mix(in srgb, var(--surface-base) 62%, var(--surface-muted) 38%)',
            }}
          />
        </section>

        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Prompt context</span>
              <h3>Sources sent with the question</h3>
              <p>Use extra context when the answer needs source comparison or validator findings.</p>
            </div>
            <Tag
              style={{
                borderColor: includedContextCount ? 'var(--border-info)' : 'var(--border-subtle)',
                background: includedContextCount ? 'var(--surface-info)' : 'var(--surface-muted)',
                color: includedContextCount ? 'var(--text-info)' : 'var(--text-secondary)',
                fontWeight: 700,
              }}
            >
              {includedContextCount ? `${includedContextCount} included` : 'Chapter only'}
            </Tag>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                padding: '8px 10px',
                border: '1px solid color-mix(in srgb, var(--border-subtle) 72%, transparent)',
                borderRadius: 8,
                background: canIncludeOriginal
                  ? includeOriginal ? 'var(--surface-info)' : 'color-mix(in srgb, var(--surface-base) 64%, var(--surface-muted) 36%)'
                  : 'var(--surface-muted)',
                opacity: canIncludeOriginal ? 1 : 0.72,
              }}
            >
              <Checkbox
                checked={includeOriginal}
                disabled={!canIncludeOriginal}
                onChange={(e) => setIncludeOriginal(e.target.checked)}
              >
                Include the original chapter text
              </Checkbox>
              <div style={{ marginTop: 3, color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.35 }}>
                {canIncludeOriginal
                  ? 'Useful for comparing a translation or proofread variant against the source chapter.'
                  : 'Original source text is not available for this variant.'}
              </div>
            </div>
          </div>
          {reviewContext && (
            <div
              style={{
                border: '1px solid color-mix(in srgb, var(--border-subtle) 72%, transparent)',
                borderRadius: 8,
                padding: 10,
                marginTop: 8,
                background: includeReviewContext ? 'var(--surface-info)' : 'color-mix(in srgb, var(--surface-base) 64%, var(--surface-muted) 36%)',
                color: 'var(--text-main)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <div>
                  <Text strong style={{ color: 'var(--text-main)' }}>{reviewContext.label || 'Review Context'}</Text>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.35 }}>
                    {replacementCount ? `${replacementCount} validator replacement${replacementCount === 1 ? '' : 's'} available.` : 'Validator reference text is available.'}
                  </div>
                </div>
                <Space align="center">
                  <Checkbox
                    checked={includeReviewContext}
                    onChange={(e) => setIncludeReviewContext(e.target.checked)}
                  >
                    Include in AI prompt
                  </Checkbox>
                  {reviewContextLoading && <Spin size="small" />}
                </Space>
              </div>
              <div
                style={{
                  border: '1px solid color-mix(in srgb, var(--border-subtle) 64%, transparent)',
                  borderRadius: 4,
                  padding: 8,
                  maxHeight: 180,
                  overflowY: 'auto',
                  background: 'var(--surface-base)',
                  marginBottom: 8,
                  color: 'var(--text-main)',
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--text-main)' }}>
                  {reviewContext.fullText || '(no reference text captured)'}
                </pre>
              </div>
              <div>
                <Text strong style={{ color: 'var(--text-main)' }}>Replacements</Text>
                {reviewContext.replacements && reviewContext.replacements.length ? (
                  <List
                    size="small"
                    dataSource={reviewContext.replacements}
                    style={{ maxHeight: 160, overflowY: 'auto', marginTop: 4 }}
                    renderItem={(rep, idx) => (
                      <List.Item key={`${idx}-${rep.from}-${rep.to}`} style={{ padding: '4px 0' }}>
                        <div>
                          {idx + 1}. {(rep.from || '').slice(0, 160)} → {(rep.to || '').slice(0, 160)}
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    No replacements captured
                  </Text>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Question</span>
              <h3>What should the AI review?</h3>
              <p>Keep the request specific so the answer is easier to evaluate and copy back into your workflow.</p>
            </div>
            <Tag
              style={{
                borderColor: hasQuestion ? 'var(--border-success)' : 'var(--border-warning)',
                background: hasQuestion ? 'var(--surface-success)' : 'var(--surface-warning)',
                color: hasQuestion ? 'var(--text-success)' : 'var(--text-warning)',
                fontWeight: 700,
              }}
            >
              {hasQuestion ? 'Ready' : 'Needed'}
            </Tag>
          </div>
          <TextArea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your question about this chapter..."
            autoSize={{ minRows: 3, maxRows: 6 }}
            disabled={sending}
          />
        </section>
      </div>
    </Modal>
  );
};

export default AskAIModal;
